import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import { useDispatch, useStore } from 'react-redux';

import { type GetFromObjectsParams, Permissions, type ScopedPermissions } from '@air/api';
import { type Workspace } from '@air/api/types';
import { type PermissionsState, permissionsSelector } from '@air/redux-permissions';

import { savePermissionsForObjectsToStore } from './savePermissionsForObjectsToStore';

function isEmpty(obj: GetFromObjectsParams<'all'>['objects']): boolean {
  return !(Object.keys(obj) as (keyof typeof obj)[]).some((key) => {
    const value = obj[key];

    if (typeof value === 'string') {
      return value !== '';
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== undefined;
  });
}

export type UseFetchPermissionsParams = {
  objects: GetFromObjectsParams<'all'>['objects'];
  excludeFetched?: boolean;
  options?: Omit<
    UseQueryOptions<
      { [key: string]: ScopedPermissions },
      unknown,
      { [key: string]: ScopedPermissions },
      ReturnType<typeof getFetchPermissionsKey>
    >,
    'queryKey'
  >;
} & ({ shortId: string } | { workspaceId: Workspace['id'] | undefined });

export const OBJECTS_PERMISSIONS = 'OBJECTS_PERMISSIONS';

export const getFetchPermissionsKey = (objects: UseFetchPermissionsParams['objects'], shortId?: string) => [
  OBJECTS_PERMISSIONS,
  { objects, shortId },
];

export const useFetchObjectsPermissions = ({
  objects,
  excludeFetched = true,
  options = {},
  ...params
}: UseFetchPermissionsParams) => {
  const store = useStore<{ permissions: PermissionsState }>();
  const dispatch = useDispatch();
  const shortId = 'shortId' in params ? params.shortId : undefined;
  const workspaceId = 'workspaceId' in params ? params.workspaceId : undefined;
  const key = getFetchPermissionsKey(objects, shortId);

  const _objects = excludeFetched
    ? (Object.keys(objects) as (keyof typeof objects)[]).reduce<typeof objects>((acc, scope) => {
        const value = objects[scope];

        if (isArray(value) && value.length > 0) {
          return {
            ...acc,
            [scope]: value.filter((objectId) => !permissionsSelector(store.getState(), scope, objectId)),
          };
        } else if (isString(value)) {
          const permissions = permissionsSelector(store.getState(), scope, value);

          return {
            ...acc,
            [scope]: !permissions ? value : undefined,
          };
        }

        return {
          ...acc,
          [scope]: value,
        };
      }, {})
    : objects;

  return useQuery({
    queryFn: async () => {
      let permissions: { [key: string]: ScopedPermissions };

      if (shortId) {
        permissions = await Permissions.getFromObjects({ mode: 'all', objects: _objects, shortId });
      } else if (workspaceId) {
        permissions = await Permissions.getFromObjects({ mode: 'all', objects: _objects, workspaceId });
      } else {
        throw new Error('No current workspace or short id');
      }

      /**
       * We iterate over all of the object scopes with values and set the query data for each.
       */
      savePermissionsForObjectsToStore(_objects, permissions, dispatch);

      return permissions;
    },
    queryKey: key,
    ...options,
    enabled: !isEmpty(_objects) && !!(options?.enabled ?? true) && !!(workspaceId || shortId),
  });
};
